import React, { useState } from 'react';

import { ReactComponent as CloseIcon } from '@pkg/assets/icons/v2/close.svg';
import { ButtonIcon } from '@pkg/components/Sections/Profile/Buttons';
import { useMediaQuery } from '@pkg/providers';

import { ViewTitle, type ViewTitleProps, ViewTitleVariant } from '../ViewTitle';

import {
    collapsedMobileHidden,
    collapsedMobilePulled,
    collapsedMobileShow,
    collapsedTabletHidden,
    collapsedTabletPulled,
    collapsedTabletShow,
} from './SheetCollapsed.motion';

import { SheetCollapsedWrapper, StyledArrow } from './SheetCollapsed.style';

export interface SheetCollapsedProps {
    title: ViewTitleProps['title'];
    flashing?: boolean;
    onMouseEnter?(): void;
    onMouseLeave?(): void;
    onExpand(): void;
    onClose(): void;
}

function getCollapsedAnimation(tablet = false, hovering = false) {
    if (hovering) {
        return tablet ? collapsedTabletPulled : collapsedMobilePulled;
    }

    return tablet ? collapsedTabletShow : collapsedMobileShow;
}

export function SheetCollapsed({
    title,
    flashing = false,
    onMouseEnter,
    onMouseLeave,
    onExpand,
    onClose,
}: SheetCollapsedProps) {
    const { tablet } = useMediaQuery();
    const [hovering, setHovering] = useState(false);

    function handleMouseEnter() {
        setHovering(true);
        onMouseEnter?.();
    }

    function handleMouseLeave() {
        setHovering(false);
        onMouseLeave?.();
    }

    return (
        <SheetCollapsedWrapper
            key={`SheetCollapsedWrapper-${tablet}`}
            initial={tablet ? collapsedTabletHidden : collapsedMobileHidden}
            exit={tablet ? collapsedTabletHidden : collapsedMobileHidden}
            animate={getCollapsedAnimation(tablet, hovering)}
            $flashing={flashing}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <ButtonIcon color="secondary" variant="solid" size="small" onClick={onExpand}>
                <StyledArrow />
            </ButtonIcon>

            <ViewTitle title={title} variant={ViewTitleVariant.Collapsed} onAction={onExpand} />

            <ButtonIcon color="secondary" variant="solid" size="small" onClick={onClose}>
                <CloseIcon />
            </ButtonIcon>
        </SheetCollapsedWrapper>
    );
}
